import React, { forwardRef, useState } from 'react';
import { Box, Container, Grid, Typography, TextField, MenuItem, Button, Paper, Alert } from '@mui/material';
import { Send as SendIcon, CheckCircle as CheckIcon } from '@mui/icons-material';
import { Reveal, HOME_COLORS, sectionLabelSx } from './common';
import { publicAPI } from '../../services/api';
import { PROGRAMS } from './ProgramsSection';

const empty = () => ({ fullName: '', phone: '', email: '', program: '', qualification: '', city: '', message: '' });

const AdmissionSection = forwardRef(function AdmissionSection({ presetProgram }, ref) {
  const [form, setForm] = useState(empty());
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  React.useEffect(() => {
    if (presetProgram) setForm((f) => ({ ...f, program: presetProgram }));
  }, [presetProgram]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.fullName.trim().length < 2) return setError('Please enter your full name.');
    if (form.phone.replace(/\D/g, '').length < 10) return setError('Please enter a valid 10-digit phone number.');
    setSubmitting(true);
    try {
      await publicAPI.submitInquiry(form);
      setDone(true);
      setForm(empty());
    } catch (err) {
      setError(err?.response?.data?.message || 'Something went wrong. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box id="admissions" ref={ref} sx={{ py: { xs: 8, md: 12 }, bgcolor: '#fff', scrollMarginTop: '72px' }}>
      <Container maxWidth="md">
        <Reveal>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography sx={sectionLabelSx}>Admissions Open</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: HOME_COLORS.dark, fontSize: { xs: '2rem', md: '2.6rem' } }}>
              Start your nursing career with us
            </Typography>
            <Typography sx={{ color: '#657875', maxWidth: 560, mx: 'auto', mt: 1.5 }}>
              Share your details below and our admissions team will get in touch to guide you through the process.
            </Typography>
          </Box>
        </Reveal>

        <Reveal delay={100}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 }, borderRadius: 5, border: '1px solid #dcebe7',
              background: 'linear-gradient(180deg, #ffffff 0%, #f4f9f8 100%)',
              boxShadow: '0 24px 48px rgba(0,77,64,0.10)',
            }}
          >
            {done ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckIcon sx={{ fontSize: 56, color: HOME_COLORS.mid, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: HOME_COLORS.dark, mb: 1 }}>Thank you!</Typography>
                <Typography sx={{ color: '#657875', mb: 3 }}>
                  We have received your inquiry. Our admissions team will contact you shortly.
                </Typography>
                <Button variant="outlined" onClick={() => setDone(false)}>Submit another inquiry</Button>
              </Box>
            ) : (
              <Box component="form" onSubmit={submit}>
                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required label="Full name" value={form.fullName} onChange={set('fullName')} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required label="Phone number" value={form.phone} onChange={set('phone')} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth type="email" label="Email address" value={form.email} onChange={set('email')} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth select label="Program interested in" value={form.program} onChange={set('program')}>
                      <MenuItem value="">Not sure yet</MenuItem>
                      {PROGRAMS.map((p) => <MenuItem key={p.code} value={p.name}>{p.name}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Current / last qualification" value={form.qualification} onChange={set('qualification')} placeholder="e.g. 12th PCB, GNM" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="City" value={form.city} onChange={set('city')} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline minRows={3} label="Message (optional)" value={form.message} onChange={set('message')} />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit" fullWidth size="large" variant="contained" disabled={submitting}
                      endIcon={<SendIcon />}
                      sx={{
                        py: 1.6, fontWeight: 700, fontSize: '1rem', borderRadius: 3,
                        background: `linear-gradient(135deg, ${HOME_COLORS.dark}, ${HOME_COLORS.sky})`,
                        '&:hover': { background: `linear-gradient(135deg, ${HOME_COLORS.deep}, ${HOME_COLORS.mid})` },
                      }}
                    >
                      {submitting ? 'Submitting...' : 'Submit inquiry'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>
        </Reveal>
      </Container>
    </Box>
  );
});

export default AdmissionSection;
